import trackingService from '../state/services/tracking';
import {
  DATA_EVENTS,
  SEGMENT_EVENTS as segmentEvents,
  PAGE_NAME as pageName,
} from './trackingConstants';

import { smoothScrollTo } from './smoothScroll';

import { BrazePurchaseEvents, logPurchaseEvent } from '../state/services/braze';

// this maintain backward compatibility for all the files using PAGE_NAME
export const PAGE_NAME = pageName;
export const SEGMENT_EVENTS = segmentEvents;
/**
 * Certain Segment events require particular fields in order to be tracked correctly when forwarded
 * to Facebook (specifically those dealing with purchasing or what Facebook considers a
 * conversions).
 *
 * @param eventId - id of event corresponding with the given tracking call
 *
 * Note: These fields are actually sent to all destinations, but other destinations typically
 *       ignore them.
 */
function getFacebookTrackingFields(eventId) {
  return {
    content_type: 'product',
    content_ids: eventId.toString(),
  };
}

export const PAGE_PARAMS = {
  // Object with countrySlug, locationSlug, sportSlug, tagSlug.
  CATEGORY_SLUGS: 'category_slugs',
};
/**
 * Track page visit.
 */
export const trackPageVisit = (name, params = {}, delay) => {
  trackingService.trackPage(name, params, delay);
};
/**
 * Track the login wall viewed event
 */
export const trackLoginWallViewed = (returnUrl) => {
  trackingService.trackEvent(DATA_EVENTS.LOGIN_WALL_VIEWED, {
    returnUrl,
  });
};

/**
 * Public methods
 *
 * These provide components with a single method to perform one or many tracking functions.
 * For example, 'login' or 'registration' methods trigger predetermined sequences of Segment calls.
 * These methods are called instead of interacting with the tracking service directly.
 *
 * TODO: These could be reworked into a decorator or HOC to save container code calling on mount.
 */

/**
 * Track an account login.
 */
export const trackAccountLogin = (profile, returnUrl) => {
  trackingService.trackDataEvent(DATA_EVENTS.ACCOUNT_LOG_IN, {
    identify: true,
    anonymousId: analytics?.user?.().anonymousId?.(),
    userId: profile.primaryIdentityId,
    returnUrl,
  });
};

/**
 * Track an account registration (and login).
 * "Account created" is tracked on both the client-side and server-side.
 * The destinations which don't support the server-side call are targetted
 * in the integrations object of the client-side call.
 */
export const trackAccountRegistrationLogin = (profile, returnUrl, source) => {
  ga('send', {
    hitType: 'event',
    eventCategory: 'All',
    eventAction: 'Account Created',
  });
  trackingService.trackDataEvent(DATA_EVENTS.ACCOUNT_REGISTRATION, {
    identify: true,
    anonymousId: analytics?.user?.().anonymousId?.(),
    userId: profile.primaryIdentityId,
    returnUrl,
    source,
  });
  setTimeout(() => {
    trackingService.trackEvent(SEGMENT_EVENTS.ACCOUNT_CREATED_CLIENT_SIDE, {});
    trackingService.trackDataEvent(DATA_EVENTS.ACCOUNT_LOG_IN, {
      userId: profile.primaryIdentityId,
      returnUrl,
      source,
    });
  }, 500);
};

/**
 * Track booking completed.
 * "Booking Completed" is tracked on both the client-side and server-side.
 * The destinations which don't support the server-side call are targetted
 * in the integrations object of the client-side call.
 */
export const trackBookingComplete = (
  ticketId,
  bookingId,
  eventId,
  friendsCount,
  revenue,
  currency,
  discount,
  creditAmount,
  source,
  paymentMethod,
  cancellationProtection,
  donationEnabled,
  donation,
) => {
  ga('send', {
    hitType: 'event',
    eventCategory: 'All',
    eventAction: 'Booking Completed',
  });
  if (revenue && currency) {
    trackingService.trackEvent(SEGMENT_EVENTS.BOOKING_COMPLETED_CLIENT_SIDE, {
      order_id: bookingId,
      revenue,
      currency,
      ...getFacebookTrackingFields(eventId),
    });
    ga('ecommerce:addTransaction', {
      id: bookingId,
      revenue,
      currency,
    });
    ga('ecommerce:send');
  } else {
    trackingService.trackEvent(
      SEGMENT_EVENTS.BOOKING_COMPLETED_CLIENT_SIDE,
      getFacebookTrackingFields(eventId),
    );
  }
  trackingService.trackDataEvent(DATA_EVENTS.BOOKING_COMPLETE, {
    ticketId,
    bookingId,
    friendsCount,
    discount,
    creditAmount,
    source,
    paymentMethod,
    cancellationProtection,
    donationEnabled,
    donation,
  });
};

/**
 * Track booking started.
 */
export const trackBookingStarted = (ticket, source) => {
  trackingService.trackDataEvent(DATA_EVENTS.BOOKING_START, {
    ticketId: ticket.id,
    source,
  });
  trackingService.trackEvent(
    SEGMENT_EVENTS.BOOKING_STARTED_CLIENT_SIDE,
    getFacebookTrackingFields(ticket.event.id),
  );
  trackPageVisit(PAGE_NAME.BOOKING_FORM_PAGE);
};

/**
 * Track event reviewed.
 */
export const trackEventReviewed = (eventId, reviewId, reviewContext) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_REVIEW, {
    eventId,
    reviewId,
    reviewContext,
  });
};
export const trackSidePanelReviewClick = () => {
  trackingService.trackEvent(DATA_EVENTS.SIDE_PANEL_REVIEW_CLICK, {});
};

/**
 * Track event saved.
 */
export const trackEventSaved = (event) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_SAVE, { eventId: event.id });
};

/**
 * Track event shared.
 */
export const trackEventShared = (event, sharedVia) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_SHARE, {
    eventId: event.id,
    sharedVia,
  });
};

/**
 * Track event unsaved.
 */
export const trackEventUnsaved = (event) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_UNSAVE, {
    eventId: event.id,
  });
};

/**
 * Track external booking saved.
 */
export const trackExternalBookingSaved = (event) => {
  trackingService.trackDataEvent(DATA_EVENTS.EXTERNALBOOKING_SAVE, {
    eventId: event.id,
  });
};

/**
 * Track external booking unsaved.
 */
export const trackExternalBookingUnsaved = (event) => {
  trackingService.trackDataEvent(DATA_EVENTS.EXTERNALBOOKING_UNSAVE, {
    eventId: event.id,
  });
};

/**
 * Track event visited.
 */
export const trackEventVisit = (event, source) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_VISIT, {
    eventId: event.id,
    source,
  });
  trackingService.trackEvent(
    SEGMENT_EVENTS.EVENT_VISITED_CLIENT_SIDE,
    getFacebookTrackingFields(event.id),
  );
  trackPageVisit(PAGE_NAME.EVENT_PAGE);
};

/**
 * Track event tickets visited.
 */
export const trackTicketsVisit = (event, source) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_TICKETS, {
    eventId: event.id,
    source,
  });
  trackPageVisit(PAGE_NAME.EVENT_TICKETS_PAGE);
};

/**
 * Track event charity entries visited.
 */
export const trackCharityEntryVisit = (event, source) => {
  trackPageVisit(PAGE_NAME.EVENT_CHARITY_ENTRY_PAGE, {
    eventId: event.id,
    source,
  });
};

/**
 * Track ticket click out.
 */
export const trackTicketClickOut = (ticket = {}) => {
  const { bookingPartner = {}, charityTickets } = ticket;
  const typeDescriptor =
    bookingPartner && bookingPartner.typeDescriptor
      ? bookingPartner.typeDescriptor
      : {};

  if (charityTickets || ['Active.com', 'tracking'].includes(typeDescriptor)) {
    const orderId = new Date().getTime();
    const currency = ticket.currencyCode;
    const ticketPrice =
      (ticket.discountPrice || ticket.rrp) + (ticket.fee || 0);
    let revenue;
    if (ticket.bookingDetails && ticket.bookingDetails.price) {
      const bookingPrice = ticket.bookingDetails && ticket.bookingDetails.price;
      revenue = Math.round((ticketPrice - bookingPrice) * 100) / 100;
    }

    const trackEvent = (event) =>
      trackingService.trackEvent(event, {
        ticketId: ticket.id,
        order_id: orderId,
        revenue,
        currency,
        ...getFacebookTrackingFields(ticket.id),
      });

    const trackGaEvent = (eventAction) =>
      ga('send', {
        hitType: 'event',
        eventCategory: 'All',
        eventAction,
      });

    const trackGaTransaction = () => {
      if (revenue && currency) {
        ga('ecommerce:addTransaction', {
          id: orderId,
          revenue,
          currency,
        });
        ga('ecommerce:send');
      }
    };

    if (charityTickets) {
      trackEvent(SEGMENT_EVENTS.CHARITY_TICKET_CLICKOUT_CLIENT_SIDE);
      trackGaEvent('Charity Ticket Clickout');
      trackGaTransaction();
    }

    if (['Active.com', 'tracking'].includes(typeDescriptor)) {
      trackEvent(SEGMENT_EVENTS.ACTIVE_TICKET_CLICKOUT_CLIENT_SIDE);
      trackGaEvent('Active Ticket Clickout');
      trackGaTransaction();
    }
  }

  trackingService.trackDataEvent(DATA_EVENTS.TICKET_CLICK_OUT, {
    ticketId: ticket.id,
  });
};

// [HoF] track scrollTo
export const trackScrollTo = (
  anchor,
  name = 'Default Scroll',
  offset = 0,
  params = {},
) => {
  return function trackAndScroll(event) {
    event.preventDefault();
    trackingService.trackEvent(name, params);
    const anchorNode = document.querySelector(anchor);
    if (!anchorNode) return;
    const yCoordinate =
      anchorNode.getBoundingClientRect().top + window.pageYOffset;
    smoothScrollTo({
      top: yCoordinate + offset,
      behavior: 'smooth',
    });
  };
};
// Do not use dash notable for parameter values. It causes them to appear strangley
// in the segment debugger.
export const INTERACTION_PARAM = {
  EVENT_ID: 'eventId',
  TOGGLED_TO: 'toggled-to',
  SOURCE: 'source',
};

// For general interaction activities that don't directly manipulate data.
// i.e. carousel-rotated, map-toggle-view-clicked, etc.
// Parameters attached will be relevent to the interaction.
// i.e. current-index, new-index, etc.
export const trackUserInteraction = (name, params = {}) => {
  trackingService.trackEvent(name, params);
};

const getBrazeEventData = (event, bookingEnabled) => {
  const organiserData =
    event.organiser && event.organiser.title
      ? { organiser_title: event.organiser.title }
      : {};
  return {
    event_id: event.id,
    event_name: event.title,
    event_start_date: event.startDate,
    event_booking_enabled: bookingEnabled,
    ...organiserData,
  };
};

export const logBookingCompletedBrazePurchaseEvent = (bookingId, ticket) => {
  if (!ticket) {
    return;
  }

  const { event, race, price, bookingEnabled } = ticket;
  logPurchaseEvent(
    BrazePurchaseEvents.BOOKING_COMPLETED,
    {
      productId: event.id,
      price: price.value / 100.0,
      currencyCode: price.currency && price.currency.code,
      quantity: 1,
    },
    {
      ...getBrazeEventData(event, bookingEnabled),
      race_id: race.id,
      booking_id: bookingId,
    },
  );
};
